import { useQueryClient, InfiniteData, UseMutationOptions } from 'react-query';
import produce from 'immer';

import {
  useGetManualNotificationsQuery,
  CreateManualNotificationMutation,
  CreateManualNotificationMutationVariables,
  GetManualNotificationsQuery,
  GetManualNotificationsQueryVariables,
  ManualNotificationsItemFragment,
} from 'api';
import { useNotify } from 'hooks';

type Mutation = CreateManualNotificationMutation;
type MutationVariables = CreateManualNotificationMutationVariables;
type QueryData = InfiniteData<GetManualNotificationsQuery>;
type QueryVariables = GetManualNotificationsQueryVariables;

const useCreateManualNotification = (variables: QueryVariables) => {
  const queryClient = useQueryClient();
  const queryKey = useGetManualNotificationsQuery.getKey(variables);
  const notify = useNotify();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: async mutationArgs => {
      await queryClient.cancelQueries(queryKey);

      const newNotification: ManualNotificationsItemFragment = {
        id: '-1',
        email_count: 0,
        user_count: 0,
        created_at: 0,
        segments: [],
        send_at: null,
        ...mutationArgs.data,
      };

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.pages[0].getManualNotificationItems.items.unshift(newNotification);
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: () => {
      void queryClient.invalidateQueries(queryKey);
    },
  };

  return mutationOptions;
};

export default useCreateManualNotification;
